import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PendingVerificationBanner from '../components/PendingVerificationBanner';
import VerifierIdentityBanner from '../components/verifiers/VerifierIdentityBanner';
import LocationGateBanner from '../components/common/LocationGateBanner';
import { Grid, Card, CardContent, Skeleton } from '@mui/material';
import {
  TrendingUp,
  Assignment,
  Person,
  PhotoLibrary,
  Search as SearchIcon,
  SmartToy,
  CheckCircle,
  Star,
  AccountBalance,
  RequestQuote
} from '@mui/icons-material';
import api from '../services/api';
import verifierService from '../services/verifierService';
import simpleVerificationService from '../services/simpleVerificationService';
import notificationService from '../services/notificationService';
import EnhancedVerifierStatCards from '../components/verifier/EnhancedVerifierStatCards';
import VerifierSmartAssistant from '../components/verifier/VerifierSmartAssistant';
import UnifiedOnboardingModal from '../components/onboarding/UnifiedOnboardingModal';
import WithdrawalDialog from '../components/common/WithdrawalDialog';
import GettingStartedChecklist from '../components/verifier/GettingStartedChecklist';
import OfflineGuideDialog from '../components/verifier/OfflineGuideDialog';
import { brand, verifier as verifierColor, text, status, border } from '../theme/colors';

const buildFallbackVerifierProfile = (user) => ({
  id: null,
  user_id: user?.id,
  first_name: user?.profile?.firstName || user?.first_name || '',
  last_name: user?.profile?.lastName || user?.last_name || '',
  email: user?.email || '',
  tier: user?.role === 'professional_verifier' ? 'professional' : 'general',
  current_assignments: 0,
  total_verifications: 0,
  completed_this_month: 0,
  completion_rate: 0,
  average_rating: 0,
  total_reviews: 0,
  earnings_this_month: 0,
  total_earnings: 0,
  average_response_time_hours: 0
});

/**
 * Enhanced Verifier Dashboard - Matches Contractor Dashboard Design
 * Modern design with Smart Insights, gradient stat cards, and gradient Quick Action buttons
 */
const VerifierDashboard_Enhanced = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const approvalStatus = user?.approvalStatus || user?.profile?.approvalStatus || 'approved';
  const isApproved = approvalStatus === 'approved';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [assignments, setAssignments] = useState({ available: 0, active: 0 });
  const [aiInsights, setAiInsights] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [profileCompleteness, setProfileCompleteness] = useState(0);
  const [showWizard, setShowWizard] = useState(false);
  const [balanceData, setBalanceData] = useState({ available: 0, pending: 0, total: 0 });
  const [withdrawalDialog, setWithdrawalDialog] = useState(false);
  const [gettingStartedDismissed, setGettingStartedDismissed] = useState(
    localStorage.getItem('eqaya_verifier_getting_started_dismissed') === 'true'
  );
  const [hasPortfolioItem, setHasPortfolioItem] = useState(false);
  const [showOfflineGuide, setShowOfflineGuide] = useState(false);
  const offlineGuideSeen = localStorage.getItem('eqaya_offline_guide_seen') === 'true';

  const refreshInsights = async () => {
    try {
      const insightsResponse = await api.get('/smart-insights/verifier-dashboard', {
        params: { refresh: 'true' }
      });
      const insightsData = insightsResponse.data;
      if (insightsData.success && insightsData.data?.insights) {
        setAiInsights(insightsData.data.insights);
        const availableInsight = insightsData.data.insights.find(i => i.type === 'opportunity');
        setAssignments(prev => ({
          ...prev,
          available: availableInsight?.data?.count || 0
        }));
      }
    } catch (err) {
      // Silently fail — insights are optional
    }
  };

  // Mark welcome as seen on first dashboard visit (enables Smart Insights for next login)
  useEffect(() => {
    if (user?.id && !user?.profile?.welcomeSeen) {
      api.put('/auth/users/profile', { welcome_seen: true }).catch(() => {});
    }
  }, [user?.id]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Subscribe to real-time assignment notifications
  useEffect(() => {
    const unsub = notificationService.on('verification-assigned', () => {
      loadDashboardData();
    });
    return unsub;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      // Check server-side onboarding flag first (survives across devices/sessions).
      // If the account-level flag says onboarding is complete we skip the profile
      // existence check, which avoids a redundant 404 round-trip.
      const onboardingComplete = user?.profile?.onboardingComplete === true;

      // Load verifier profile
      let profileData;
      try {
        profileData = await verifierService.getMyProfile();
      } catch (profileError) {
        const profileMissing = (
          profileError.message?.toLowerCase().includes('not found') ||
          profileError.message?.includes('404') ||
          profileError.response?.status === 404
        );

        // If 404 or "not found" error, trigger onboarding wizard only when the
        // server-side flag also says it's not complete.
        if (!onboardingComplete && profileMissing) {
          setLoading(false);
          setShowWizard(true);
          return;
        }
        if (!profileMissing) {
          throw profileError;
        }
        // If onboarding is marked complete but profile 404s, keep the dashboard
        // renderable and let profile-specific CTAs guide the user back to setup.
        profileData = buildFallbackVerifierProfile(user);
      }

      // If no profile exists and onboarding flag is false, trigger wizard
      if (!profileData && !onboardingComplete) {
        setLoading(false);
        setShowWizard(true);
        return;
      }

      if (!profileData) {
        profileData = buildFallbackVerifierProfile(user);
      }
      
      setProfile(profileData);

      // Fetch real profile completeness for the Profile Strength banner
      try {
        const completenessData = await verifierService.getMyProfileCompleteness();
        setProfileCompleteness(completenessData?.completionPercentage ?? completenessData?.completion_percentage ?? 0);
      } catch (compErr) {
        console.error('[Dashboard] Error fetching profile completeness:', compErr);
      }

      // Calculate stats
      const calculatedStats = {
        currentAssignments: profileData.current_assignments || 0,
        totalVerifications: profileData.total_verifications || 0,
        completedThisMonth: profileData.completed_this_month || 0,
        completionRate: parseFloat(profileData.completion_rate) || 0,
        averageRating: parseFloat(profileData.average_rating) || 0,
        totalReviews: profileData.total_reviews || 0,
        earningsThisMonth: profileData.earnings_this_month || 0,
        totalEarnings: profileData.total_earnings || 0,
        averageResponseTime: profileData.average_response_time_hours || 0
      };
      setStats(calculatedStats);

      // Fetch real AI insights from backend
      try {
        const insightsResponse = await api.get('/smart-insights/verifier-dashboard');
        const insightsData = insightsResponse.data;

        if (insightsData.success && insightsData.data?.insights) {
          setAiInsights(insightsData.data.insights);

          // Extract real assignments count from insights
          const availableInsight = insightsData.data.insights.find(i => i.type === 'opportunity');
          setAssignments({
            available: availableInsight?.data?.count || 0,
            active: profileData.current_assignments || 0
          });
        } else {
          setAiInsights([]);
          setAssignments({ available: 0, active: profileData.current_assignments || 0 });
        }
      } catch (insightsError) {
        console.error('[Dashboard] Error fetching AI insights:', insightsError);
        // Fallback to empty insights
        setAiInsights([]);
        setAssignments({
          available: 0,
          active: profileData.current_assignments || 0
        });
      }

      // Load pending verifications (assigned status)
      try {
        const response = await simpleVerificationService.getMyVerifications('assigned');
        if (response.success && response.data) {
          setPendingVerifications(response.data);
        } else {
          setPendingVerifications([]);
        }
      } catch (pendingError) {
        console.error('[Dashboard] Error fetching pending verifications:', pendingError);
        setPendingVerifications([]);
      }

      // Fetch verifier balance data for the balance card
      try {
        const earningsResponse = await api.get('/reports/verifier/earnings');
        if (earningsResponse.data?.success) {
          const summary = earningsResponse.data.data?.summary || {};
          setBalanceData({
            available: parseFloat(summary.withdrawableBalance) || 0,
            pending: parseFloat(summary.pendingEarnings) || 0,
            total: parseFloat(summary.totalEarnings) || 0
          });
        }
      } catch (balanceError) {
        console.error('[Dashboard] Error fetching balance data:', balanceError);
      }

      // Check if verifier has any portfolio items
      try {
        const portfolioResponse = await api.get('/verifiers/profile/me/portfolio');
        if (portfolioResponse.data?.data?.length > 0) {
          setHasPortfolioItem(true);
        }
      } catch (portfolioError) {
        // Silently fail — portfolio check is optional
      }

    } catch (err) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleWizardComplete = async (data) => {
    // Save profile data via wizard
    try {
      if (profile && profile.id) {
        // Update existing profile
        await verifierService.updateProfile(profile.id, data);
        setProfile(prev => ({ ...prev, ...data }));
      } else {
        // Create new profile
        const response = await verifierService.createProfile(data);
        setProfile(response.data);
      }

      // Persist onboarding completion server-side so this survives across
      // devices and sessions. Also update local AuthContext state immediately.
      await api.put('/auth/users/profile', { onboarding_complete: true });
      updateUser({ profile: { ...user?.profile, onboardingComplete: true } });

      setShowWizard(false);
      loadDashboardData(); // Reload to reflect changes
    } catch (err) {
      console.error('Failed to save profile from wizard:', err);
      alert('Failed to save profile: ' + (err.message || 'Please try again'));
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={400} height={24} sx={{ mb: 3 }} />
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="40%" height={32} sx={{ my: 1 }} />
                  <Skeleton variant="text" width="80%" height={16} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px', mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: '12px' }} />
      </div>
    );
  }

  // Show onboarding wizard if no profile (handled by loadDashboardData)
  if (!profile && showWizard) {
    return <UnifiedOnboardingModal userRole="verifier" />;
  }

  if (!profile) {
    return (
      <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '15px',
          borderRadius: '8px',
          border: '1px solid #ffeeba'
        }}>
          No profile found. Please contact support.
        </div>
      </div>
    );
  }

  // Calculate Trust Score (Profile Strength) from real profile completeness
  const trustScore = Math.round(profileCompleteness || 0);
  const isProfessional = profile.tier === 'professional';

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Onboarding Wizard */}
      {showWizard && <UnifiedOnboardingModal userRole="verifier" />}

      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ color: text.primary, marginBottom: '10px', fontSize: '32px', fontWeight: '700' }}>
          Dashboard
        </h1>
        <p style={{ color: text.secondary, fontSize: '16px' }}>
          Manage your verification assignments and track your performance
        </p>
      </div>

      {/* Pending Verification Banner — shown when account is not yet approved */}
      <PendingVerificationBanner approvalStatus={approvalStatus} />

      {/* Identity-document banner — prompts upload of a government ID when missing */}
      <VerifierIdentityBanner />

      {/* Location gate — shown when Province/District are missing (blocks proposals) */}
      <LocationGateBanner />

      {error && (
        <div style={{
          backgroundColor: '#fdf2f2',
          color: status.errorDark,
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #fecaca'
        }}>
          {error}
        </div>
      )}

      {/* Gamified Profile Completion Banner / Trust Score Meter — hidden when complete */}
      {!isProfessional && trustScore < 100 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '25px',
          marginBottom: '30px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          border: `1px solid ${border.light}`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <div>
              <h2 style={{ margin: '0 0 5px 0', fontSize: '24px', display: 'flex', alignItems: 'center', gap: '10px', color: text.primary }}>
                <TrendingUp sx={{ fontSize: 28, color: verifierColor.primary }} /> Profile Strength: {trustScore}%
              </h2>
              <p style={{ margin: 0, color: text.secondary, fontSize: '16px' }}>
                You are {trustScore}% complete. Verify your ID and qualifications to reach 100% and unlock professional verification work.
              </p>
            </div>
            <button
              onClick={() => setShowWizard(true)}
              style={{
                backgroundColor: 'white',
                color: verifierColor.primary,
                border: `2px solid ${verifierColor.primary}`,
                padding: '12px 24px',
                borderRadius: '8px',
                fontWeight: '700',
                fontSize: '16px',
                cursor: 'pointer',
                transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => { e.target.style.backgroundColor = verifierColor.primary; e.target.style.color = 'white'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = 'white'; e.target.style.color = verifierColor.primary; }}
            >
              Complete Profile →
            </button>
          </div>

          {/* Progress Bar */}
          <div style={{ width: '100%', height: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              width: `${trustScore}%`,
              height: '100%',
              backgroundColor: verifierColor.primary,
              borderRadius: '4px',
              transition: 'width 1s ease-in-out'
            }} />
          </div>
        </div>
      )}

      {/* Getting Started Checklist */}
      {!gettingStartedDismissed && (
        <GettingStartedChecklist
          profileComplete={profileCompleteness >= 100}
          hasPortfolioItem={hasPortfolioItem}
          hasBrowsedServer={user?.profile?.browsedVerifications || user?.profile?.browsed_verifications}
          hasAcceptedVerification={(stats?.totalVerifications || 0) > 0 || pendingVerifications.length > 0}
          hasCompletedVerification={(stats?.completedThisMonth || 0) > 0 || (stats?.totalVerifications || 0) > 0}
          onDismiss={() => {
            setGettingStartedDismissed(true);
            localStorage.setItem('eqaya_verifier_getting_started_dismissed', 'true');
          }}
        />
      )}

      {/* Offline Mode Awareness Banner */}
      {!offlineGuideSeen && (
        <Card
          sx={{
            mb: 3,
            borderLeft: `4px solid ${brand.primary || '#667eea'}`,
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            cursor: 'pointer'
          }}
          onClick={() => setShowOfflineGuide(true)}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
            <span style={{ fontSize: 28 }}>&#128246;</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: '#2c3e50', marginBottom: 2 }}>
                New: Complete Verifications Offline
              </div>
              <div style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>
                Going to a site with poor internet? Prepare beforehand and complete the entire verification without any connection.
              </div>
            </div>
            <span style={{ color: brand.primary || '#667eea', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              Learn How &rarr;
            </span>
          </CardContent>
        </Card>
      )}

      {/* Offline Guide Dialog */}
      <OfflineGuideDialog
        open={showOfflineGuide}
        onClose={() => setShowOfflineGuide(false)}
      />

      {/* Smart Insights — hidden on first login */}
      {user?.profile?.welcomeSeen && (
        <div style={{ marginBottom: '30px' }}>
          <VerifierSmartAssistant
            profile={profile}
            stats={stats}
            assignments={assignments}
            aiInsights={aiInsights}
            onRefresh={refreshInsights}
          />
        </div>
      )}

      {/* Enhanced Stats Cards */}
      <div style={{ marginBottom: '30px' }}>
        <EnhancedVerifierStatCards stats={stats} />
      </div>

      {/* Balance Card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '25px',
        marginBottom: '30px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: `2px solid ${verifierColor.primary}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <AccountBalance sx={{ fontSize: 40, color: verifierColor.primary, opacity: 0.7 }} />
          <div>
            <div style={{ color: text.secondary, fontSize: '14px', marginBottom: '4px' }}>
              Available Balance
            </div>
            <div style={{ fontSize: '32px', fontWeight: '700', color: verifierColor.primary, lineHeight: 1.2 }}>
              ${balanceData.available.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            {balanceData.pending > 0 && (
              <div style={{ color: status.warning, fontSize: '14px', marginTop: '4px' }}>
                ${balanceData.pending.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pending
              </div>
            )}
          </div>
        </div>
        <button
          onClick={() => setWithdrawalDialog(true)}
          disabled={balanceData.available <= 0}
          style={{
            padding: '14px 28px',
            backgroundColor: balanceData.available <= 0 ? '#9CA3AF' : verifierColor.primary,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: balanceData.available <= 0 ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: '700',
            transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: balanceData.available <= 0 ? 'none' : '0 4px 12px rgba(67, 181, 129, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            opacity: balanceData.available <= 0 ? 0.6 : 1
          }}
          onMouseEnter={(e) => {
            if (balanceData.available <= 0) return;
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(67, 181, 129, 0.4)';
          }}
          onMouseLeave={(e) => {
            if (balanceData.available <= 0) return;
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(67, 181, 129, 0.3)';
          }}
        >
          <RequestQuote sx={{ fontSize: 20 }} /> Withdraw
        </button>
      </div>

      {/* Withdrawal Dialog */}
      <WithdrawalDialog
        open={withdrawalDialog}
        onClose={() => setWithdrawalDialog(false)}
        availableBalance={balanceData.available}
        role="verifier"
        userId={user?.id}
        onSuccess={() => loadDashboardData()}
      />

      {/* Pending Verifications Section */}
      {pendingVerifications && pendingVerifications.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '25px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          marginBottom: '30px'
        }}>
          <h2 style={{
            color: text.primary,
            marginTop: 0,
            marginBottom: '20px',
            fontSize: '20px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <Assignment sx={{ fontSize: 24, color: text.primary }} /> Pending Verifications
            <span style={{
              backgroundColor: status.errorDark,
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '700'
            }}>
              {pendingVerifications.length}
            </span>
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {pendingVerifications.map(verif => (
              <div key={verif.id} style={{
                padding: '16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = verifierColor.primary;
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(67, 181, 129, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '16px' }}>
                    {verif.title}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                    {verif.description}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: '13px',
                      color: verifierColor.primary,
                      backgroundColor: '#f0fdf4',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontWeight: '600',
                      border: '1px solid #bbf7d0'
                    }}>
                      ${verif.cost}
                    </span>
                    <span style={{
                      fontSize: '13px',
                      color: brand.secondary,
                      backgroundColor: '#f5f0ff',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: '1px solid #e1d5f5'
                    }}>
                      {verif.verificationType?.replace('_', ' ').toUpperCase()}
                    </span>
                    {verif.tier && (
                      <span style={{
                        fontSize: '13px',
                        color: status.warning,
                        backgroundColor: '#fffbeb',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #fde68a'
                      }}>
                        {verif.tier.charAt(0).toUpperCase() + verif.tier.slice(1)} Tier
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/verifier/verify/${verif.id}`)}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: verifierColor.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '15px',
                    transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 4px 12px rgba(67, 181, 129, 0.3)',
                    marginLeft: '16px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(67, 181, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(67, 181, 129, 0.3)';
                  }}
                >
                  Start Now →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div style={{
        backgroundColor: 'white',
        padding: '25px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        marginBottom: '30px'
      }}>
        <h2 style={{ color: text.primary, marginTop: 0, marginBottom: '20px', fontSize: '20px', fontWeight: '600' }}>
          Quick Actions
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <button
            onClick={() => navigate('/verifier/profile')}
            style={{
              padding: '16px',
              backgroundColor: 'white',
              color: text.primary,
              border: `2px solid ${border.light}`,
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
              e.target.style.borderColor = verifierColor.primary;
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
              e.target.style.borderColor = border.light;
            }}
          >
            <Person sx={{ fontSize: 20, verticalAlign: 'middle', mr: 0.5 }} /> My Profile
          </button>
          <button
            onClick={() => navigate('/verifier/portfolio')}
            style={{
              padding: '16px',
              backgroundColor: 'white',
              color: text.primary,
              border: `2px solid ${border.light}`,
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
              e.target.style.borderColor = verifierColor.primary;
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
              e.target.style.borderColor = border.light;
            }}
          >
            <PhotoLibrary sx={{ fontSize: 20, verticalAlign: 'middle', mr: 0.5 }} /> My Portfolio
          </button>
          {isApproved && (
            <button
              onClick={() => navigate('/verifier/available-verifications')}
              style={{
                padding: '16px',
                backgroundColor: 'white',
                color: text.primary,
                border: `2px solid ${border.light}`,
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                e.target.style.borderColor = verifierColor.primary;
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                e.target.style.borderColor = border.light;
              }}
            >
              <SearchIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 0.5 }} /> Available Verifications
            </button>
          )}
          <button
            onClick={() => navigate('/verifier/my-verifications')}
            style={{
              padding: '16px',
              backgroundColor: 'white',
              color: text.primary,
              border: `2px solid ${border.light}`,
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
              e.currentTarget.style.borderColor = verifierColor.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
              e.currentTarget.style.borderColor = border.light;
            }}
          >
            <Assignment sx={{ fontSize: 20, verticalAlign: 'middle', mr: 0.5 }} /> My Verifications
            {pendingVerifications.length > 0 && (
              <span style={{
                backgroundColor: status.errorDark,
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '700',
                minWidth: '20px',
                textAlign: 'center'
              }}>
                {pendingVerifications.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Active Assignments Section */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '30px' }}>
        <div style={{ 
          backgroundColor: 'white', 
          padding: '25px', 
          borderRadius: '12px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)' 
        }}>
          <h2 style={{ color: text.primary, marginBottom: '20px', fontSize: '20px', fontWeight: '600' }}>
            Active Assignments
          </h2>

          {assignments.active === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: text.secondary }}>
              <div style={{ marginBottom: '15px' }}><CheckCircle sx={{ fontSize: 48, color: verifierColor.primary }} /></div>
              <h3 style={{ marginBottom: '10px' }}>No Active Assignments</h3>
              <p style={{ marginBottom: '20px' }}>Browse available verification opportunities and start work from one place</p>
              {isApproved && (
                <button
                  onClick={() => navigate('/verifier/available-verifications')}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: verifierColor.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                >
                  Find Work
                </button>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: text.secondary }}>
              <div style={{ marginBottom: '10px' }}><Assignment sx={{ fontSize: 36, color: text.secondary }} /></div>
              <p>You have {assignments.active} active assignment{assignments.active !== 1 ? 's' : ''}</p>
              <button
                onClick={() => navigate('/verifier/assignments')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: verifierColor.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginTop: '15px'
                }}
              >
                View Assignments
              </button>
            </div>
          )}
        </div>

        {/* Quick Stats Sidebar */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '25px', 
          borderRadius: '12px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)' 
        }}>
          <h2 style={{ color: text.primary, marginBottom: '20px', fontSize: '20px', fontWeight: '600' }}>
            Quick Stats
          </h2>
          
          <div style={{ marginBottom: '20px' }}>
            <div style={{ color: text.secondary, fontSize: '14px', marginBottom: '5px' }}>Completion Rate</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: verifierColor.primary }}>
              {stats?.completionRate?.toFixed(1) || 0}%
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ color: text.secondary, fontSize: '14px', marginBottom: '5px' }}>Average Rating</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: status.warning }}>
              {stats?.averageRating?.toFixed(1) || 0} <Star sx={{ fontSize: 28, color: status.warning, verticalAlign: 'middle' }} />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <div style={{ color: text.secondary, fontSize: '14px', marginBottom: '5px' }}>This Month</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: verifierColor.primary }}>
              {stats?.completedThisMonth || 0} Completed
            </div>
          </div>

          <div>
            <div style={{ color: text.secondary, fontSize: '14px', marginBottom: '5px' }}>Available</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#16a085' }}>
              {assignments.available} New
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifierDashboard_Enhanced;
